import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Advertisement } from '@/components/Icons/Advertisement';
import { Snackbar } from '@/components/Snackbar';
import { Toggle } from '@/components/Toggle';
import { useUserContext } from '@/auth/UserContext';
import { useUserSettings } from '@/hooks/useUserSettings';
import { SnackbarVariant } from '@/types/components';
import { UserSetting } from '@/types/user';

interface RowProps {
  title: string;
  subtitle: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const SwitcherRow: React.FC<RowProps> = ({
  title,
  subtitle,
  checked,
  disabled,
  onCheckedChange,
}) => (
  <div className="flex flex-1 items-center gap-3 px-6 py-4">
    <Advertisement className="size-4 shrink-0 text-primary" />
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <p className="text-body-16 text-foreground">{title}</p>
      <p className="text-body-14 text-subtle-foreground">{subtitle}</p>
    </div>
    <Toggle
      checked={checked}
      disabled={disabled}
      label={title}
      onCheckedChange={onCheckedChange}
    />
  </div>
);

export const Switchers: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const { updateSetting } = useUserSettings({
    onError: () => setIsErrorSnackbarOpen(true),
  });

  const { premiumSubscription, userSettings } = user ?? {};

  const isSubscribed = premiumSubscription?.isActive ?? false;
  const isAdvertisingEnabled = userSettings?.isAdvertisingEnabled ?? false;
  const isCommercialNewsSubscriber =
    userSettings?.isCommercialNewsSubscriber ?? false;

  const adsSubtitle = t(
    isSubscribed
      ? 'subscription.intro.switchers.adsSubtitle'
      : 'subscription.intro.switchers.disabledSubtitle',
  );
  const commercialNewsSubtitle = t(
    isSubscribed
      ? 'subscription.intro.switchers.commercialNewsSubtitle'
      : 'subscription.intro.switchers.disabledSubtitle',
  );

  return (
    <>
      <div className="flex h-full flex-col divide-y divide-divider rounded-2xl bg-card shadow-card">
        <SwitcherRow
          title={t('subscription.intro.switchers.commercialNewsTitle')}
          subtitle={commercialNewsSubtitle}
          checked={isCommercialNewsSubscriber}
          disabled={!isSubscribed}
          onCheckedChange={(value) =>
            updateSetting({ setting: UserSetting.CommercialNews, value })
          }
        />
        <SwitcherRow
          title={t('subscription.intro.switchers.adsTitle')}
          subtitle={adsSubtitle}
          checked={isAdvertisingEnabled}
          disabled={!isSubscribed}
          onCheckedChange={(value) =>
            updateSetting({ setting: UserSetting.Advertising, value })
          }
        />
      </div>

      <Snackbar
        isOpen={isErrorSnackbarOpen}
        compact
        message={t('global.errorOccurred')}
        variant={SnackbarVariant.Error}
        onDismiss={() => setIsErrorSnackbarOpen(false)}
      />
    </>
  );
};
